import React from 'react'
import { cn } from '../lib/utils'
import {
 defaultCurrencySymbol,
 formatCurrencyMetricParts,
 formatMetricNumber,
 getMetricValueSizeClass,
 parseMetricValueString,
 type MetricKpiTier,
} from '../../helpers/formatMetricDisplay'

type DashboardMetricValueProps = {
 metricValue?: number | string | null
 isCurrency?: boolean
 currencySymbol?: string
 /** Legacy pre-formatted string when metricValue is omitted */
 value?: string | number
 kpiTier: MetricKpiTier
 compact?: boolean
 /** When true, renders the unavailable label instead of a numeric zero. */
 unavailable?: boolean
 unavailableLabel?: string
}

export function DashboardMetricValue({
 metricValue,
 isCurrency,
 currencySymbol,
 value,
 kpiTier,
 compact,
 unavailable = false,
 unavailableLabel = '—',
}: DashboardMetricValueProps) {
 const resolvedCurrencySymbol = currencySymbol ?? defaultCurrencySymbol()

 if (unavailable) {
  return (
   <p
    className={cn(
     compact ? 'mt-0.5' : 'mt-1',
     'text-sm font-semibold text-muted-foreground',
    )}
   >
    {unavailableLabel}
   </p>
  )
 }

 let display: {
 isCurrency: boolean
 amount: string
 currency?: string
 full: string
 isNegative: boolean
 }

 if (metricValue !== undefined && metricValue !== null && metricValue !== '') {
 if (isCurrency) {
 const parts = formatCurrencyMetricParts(metricValue, resolvedCurrencySymbol)
 display = {
 isCurrency: true,
 amount: parts.amount,
 currency: parts.currency,
 full: parts.full,
 isNegative: parts.isNegative,
 }
 } else {
 const amount = formatMetricNumber(metricValue)
 display = {
 isCurrency: false,
 amount,
 full: amount,
 isNegative: Number(metricValue) < 0,
 }
 }
 } else {
 display = parseMetricValueString(String(value ?? ''))
 }

 const sizeClass = getMetricValueSizeClass(display.full, kpiTier)

 if (display.isCurrency && display.currency) {
  return (
   <p
    className={cn(
     compact ? 'mt-0.5' : 'mt-1',
     'flex w-full min-w-0 max-w-full flex-wrap items-baseline justify-start gap-x-1.5 leading-tight',
     sizeClass,
    )}
    title={display.full}
   >
    <span
     dir="ltr"
     className={cn(
      'min-w-0 truncate font-extrabold tabular-nums tracking-tight text-foreground',
      display.isNegative && 'text-semantic-danger',
     )}
    >
     {display.amount}
    </span>
    <span
     className={cn(
      'shrink-0 text-xs font-semibold leading-none text-muted-foreground sm:text-sm',
      compact && 'text-[11px] sm:text-xs',
     )}
    >
     {display.currency}
    </span>
   </p>
  )
 }

 return (
  <p
   className={cn(
    compact ? 'mt-0.5' : 'mt-1',
    'w-full min-w-0 max-w-full text-start leading-tight',
    sizeClass,
   )}
   title={display.full}
  >
   <span
    dir="ltr"
    className={cn(
     'inline-block font-extrabold tabular-nums tracking-tight text-foreground',
     display.isNegative && 'text-semantic-danger',
    )}
   >
    {display.amount}
   </span>
  </p>
 )
}
